import { useEffect } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";

/**
 * This hook triggers the validation of a field whenever it changes, if it's in an error state.
 * Typically, we want the first validation of a form field to be onBlur, but if it's in an error
 * state, we want to remove the error as soon as the user changes the value into a correct one.
 *
 * For example, if we have an email field and the user inserts "mymail@mail" and then leaves the
 * field (blur event), the field will be in an error state (wrong format).
 * As soon as the user returns on the field and changes its value to "mymail@mail.com" the field
 * must not be in an error state anymore. This hook will detect the change event (thanks to the
 * subscription to the watch method) and trigger validation for the changed field.
 *
 * @param formMethods the return value of useForm(...)
 */
export function useRevalidateFormOnChange<T extends FieldValues>(formMethods: UseFormReturn<T>) {
  const { trigger, watch, getFieldState } = formMethods;

  useEffect(() => {
    const subscription = watch((_, { name, type }) => {
      if (!name || type !== "change") return;
      const { error, isTouched } = getFieldState(name);
      if (error || isTouched) {
        trigger(name);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getFieldState, trigger]);
}
